import { TRPCUntypedClient, getUntypedClient } from '@trpc/client';
import { createTRPCRecursiveProxy, callTRPCProcedure } from '@trpc/server';
import { trpcInfiniteQueryOptions } from './infiniteQueryOptions.mjs';
import { trpcMutationOptions } from './mutationOptions.mjs';
import { trpcQueryOptions } from './queryOptions.mjs';
import { trpcSubscriptionOptions } from './subscriptionOptions.mjs';
import { getQueryKeyInternal, getMutationKeyInternal, unwrapLazyArg } from './utils.mjs';

/**
 * Create a typed proxy from your router types. Can also be used on the server.
 *
 * @see https://trpc.io/docs/client/tanstack-react-query/setup#3b-setup-without-react-context
 * @see https://trpc.io/docs/client/tanstack-react-query/server-components#5-create-a-trpc-caller-for-server-components
 */ function createTRPCOptionsProxy(opts) {
    const callIt = (type)=>{
        return (path, input, trpcOpts)=>{
            if ('router' in opts) {
                return Promise.resolve(unwrapLazyArg(opts.ctx)).then((ctx)=>callTRPCProcedure({
                        router: opts.router,
                        path: path,
                        getRawInput: async ()=>input,
                        ctx: ctx,
                        type: type,
                        signal: undefined
                    }));
            }
            const untypedClient = opts.client instanceof TRPCUntypedClient ? opts.client : getUntypedClient(opts.client);
            return untypedClient[type](path, input, trpcOpts);
        };
    };
    return createTRPCRecursiveProxy(({ args, path: _path })=>{
        const path = [
            ..._path
        ];
        const utilName = path.pop();
        const [arg1, arg2] = args;
        const contextMap = {
            '~types': undefined,
            pathKey: ()=>{
                return getQueryKeyInternal(path);
            },
            pathFilter: ()=>{
                return {
                    ...arg1,
                    queryKey: getQueryKeyInternal(path)
                };
            },
            queryOptions: ()=>{
                return trpcQueryOptions({
                    input: arg1,
                    opts: arg2,
                    path,
                    queryClient: opts.queryClient,
                    queryKey: getQueryKeyInternal(path, arg1, 'query'),
                    query: callIt('query')
                });
            },
            queryKey: ()=>{
                return getQueryKeyInternal(path, arg1, 'query');
            },
            queryFilter: ()=>{
                return {
                    ...arg2,
                    queryKey: getQueryKeyInternal(path, arg1, 'query')
                };
            },
            infiniteQueryOptions: ()=>{
                return trpcInfiniteQueryOptions({
                    input: arg1,
                    opts: arg2,
                    path,
                    queryClient: opts.queryClient,
                    queryKey: getQueryKeyInternal(path, arg1, 'infinite'),
                    query: callIt('query')
                });
            },
            infiniteQueryKey: ()=>{
                return getQueryKeyInternal(path, arg1, 'infinite');
            },
            infiniteQueryFilter: ()=>{
                return {
                    ...arg2,
                    queryKey: getQueryKeyInternal(path, arg1, 'infinite')
                };
            },
            mutationOptions: ()=>{
                return trpcMutationOptions({
                    opts: arg1,
                    path,
                    queryClient: opts.queryClient,
                    mutate: callIt('mutation'),
                    overrides: opts.overrides?.mutations
                });
            },
            mutationKey: ()=>{
                return getMutationKeyInternal(path);
            },
            subscriptionOptions: ()=>{
                return trpcSubscriptionOptions({
                    opts: arg2,
                    path,
                    queryKey: getQueryKeyInternal(path, arg1, 'any'),
                    subscribe: callIt('subscription')
                });
            }
        };
        return contextMap[utilName]();
    });
}

export { createTRPCOptionsProxy };
